import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router"

import { useLogout, useUser } from "@/auth/hooks"
import { hasPublishedPosts } from "@/blog/content"
import { LanguageSwitcher, SiteFooter, SiteNav, SkipLink } from "@/brand/components/landing"
import { FOOTER_COLUMNS, HEADER_NAV, isNavGroup, LEGAL_LINKS, withoutBlog } from "@/brand/nav-model"
import { ProfileMenu } from "@/core/components/profile-menu"
import { localeOfPath } from "@/core/i18n/locale"
import { LocaleProvider, useUi } from "@/core/i18n/locale-context"
import { isComingSoon } from "@/core/launch"

import { localizeFooterColumns, localizeLegalLinks, localizeNavEntries } from "../nav-model.zh"

export const Route = createFileRoute("/_brand-layout")({
  component: ContentLayout,
})

const MAIN_ID = "main-content"

// Menu and footer data: @/brand/nav-model (T816). The blog link stays hidden until the first post.
const NAV_ITEMS = HEADER_NAV.map((e) =>
  isNavGroup(e) ? { ...e, items: withoutBlog(e.items, hasPublishedPosts) } : e,
)
const FOOTER = FOOTER_COLUMNS.map((c) => ({ ...c, links: withoutBlog(c.links, hasPublishedPosts) }))

/** The language comes from the URL, so the prerendered HTML and the hydrated page agree. */
function ContentLayout() {
  const { pathname } = useLocation()
  return (
    <LocaleProvider locale={localeOfPath(pathname)}>
      <LayoutBody pathname={pathname} />
    </LocaleProvider>
  )
}

function LayoutBody({ pathname }: { pathname: string }) {
  const zh = localeOfPath(pathname) === "zh"
  const ui = useUi()
  const logout = useLogout()
  const { data: userData, isPending, failureCount } = useUser()
  const user = userData?.user
  const signedIn = Boolean(user) && !isComingSoon()
  // The prerendered HTML cannot know the session: hide Sign in and the CTA until it is known (CSS reveals them after 3 s even without JS).
  // a failing session lookup (retrying) must not leave the buttons hidden: treat it as signed out
  const authState = isPending && failureCount === 0 ? "pending" : signedIn ? "in" : "out"
  return (
    <>
      <SkipLink targetId={MAIN_ID} />
      <SiteNav
        items={zh ? localizeNavEntries(NAV_ITEMS) : NAV_ITEMS}
        signInHref="/login"
        ctaHref={signedIn ? "/trips" : "/login"}
        ctaLabel={signedIn ? ui.dashboard : ui.startTrial}
        authState={authState}
        accountMenu={
          user ? (
            <ProfileMenu
              compact
              name={user.name}
              email={user.email}
              onSignOut={() => void logout()}
            />
          ) : null
        }
        languageSwitcher={<LanguageSwitcher pathname={pathname} />}
      />
      <main id={MAIN_ID} tabIndex={-1} className="outline-none">
        <Outlet />
      </main>
      <SiteFooter
        columns={zh ? localizeFooterColumns(FOOTER) : FOOTER}
        legalLinks={zh ? localizeLegalLinks(LEGAL_LINKS) : LEGAL_LINKS}
      />
    </>
  )
}
